import { z } from 'zod';
import { extractAsin } from '../utils/normalize.js';

/**
 * Doğrudan ürün ekleme — girdi ayrıştırma.
 *
 * **Neden var:** Kullanıcı bazen ASIN'lerin uygun olduğunu **zaten biliyor**
 * (başka bir yazılımdan ya da kendi taramasından). O ASIN'leri keşif
 * hattından geçirmek, bilinen bir cevabı yeniden satın almak demek —
 * ayda 300-400 bin ürün tarayan biri için gerçek para.
 *
 * Bu yol Keepa tokeni **harcamıyor**: kaynak verisi çekilmiyor, ürün
 * doğrudan mağazaya yazılıyor.
 *
 * ⚠️ Tokensiz olmak, kontrolsüz olmak demek değil. Hesabı riske atan
 * kontroller (kara liste, "listeleyebilir miyim?") token gerektirmiyor ve
 * yerinde duruyor — bkz. `DirectAddService`.
 */

export interface DirectAddLine {
  asin: string;
  /** Alış fiyatı (USD). Başka yazılımdan geliyorsa fiyat da gelmiş olabilir. */
  sourcePriceUsd: number | null;
  /** Başlangıç stok adedi. */
  qty: number | null;
  /** Ham satır — hata mesajında kullanıcıya gösteriliyor. */
  raw: string;
}

export interface ParsedDirectAdd {
  lines: DirectAddLine[];
  /** ASIN'i okunamayan satırlardan ilk 50'si (ekranda örnek olarak). */
  invalid: string[];
  /** ASIN'i okunamayan satırların tamamının sayısı; `invalid` 50'de kesiliyor. */
  invalidCount: number;
  totalLines: number;
  duplicates: number;
  /** Satır sınırını aştığı için hiç okunmayan satır sayısı. */
  droppedOverLimit: number;
}

/** Tek listede en fazla kaç ürün eklenebilir. */
export const DIRECT_ADD_SATIR_SINIRI = 100_000;

/** Yüklenen Excel'in üst sınırı: 100.000 satır × 3 sütun ~2,2 MB; neden sınır: `direct-add-dosya.ts`. */
export const DIRECT_ADD_EXCEL_SINIRI_BAYT = 4 * 1024 * 1024;

/** Yüklenen CSV/metin dosyasının üst sınırı (base64 öncesi); akıtmadan satır satır okunuyor. */
export const DIRECT_ADD_DOSYA_SINIRI_BAYT = 15_000_000;

/**
 * Satırı sütunlara böler.
 *
 * ⚠️ 2 Ekim 2026 üretim: sahip Türkçe Excel'den `ASIN<sekme>79,99` yapıştırdı
 * (ondalık virgül). Eski ayırıcı `[,;\t|]` virgülü de sütun sayıyordu: `79` fiyat,
 * `99` ADET okundu. 43.474 satırın 41.511'inde fiyat kuruşsuz, stok kuruş kadar
 * (1–99) yazıldı ve hiçbir yerde uyarı çıkmadı.
 *
 * Kural: satırda sekme varsa sütunlar YALNIZ sekmeyle, yoksa noktalı virgül ya
 * da dikey çizgiyle ayrılıyor; virgül o zaman sayının içinde (bkz. `sayiMetni`).
 * Virgül ancak başka ayırıcı yoksa sütun ayırıcı: `B08N5WRWNW,25.99,3`.
 */
function satiriBol(satir: string): string[] {
  if (satir.includes('\t')) return satir.split('\t');
  if (/[;|]/.test(satir)) return satir.split(/[;|]/);
  return satir.split(',');
}

/**
 * Hücredeki sayıyı JavaScript biçimine çevirir: `79,99` → `79.99`,
 * `1.234,56` → `1234.56`, `1,234.56` → `1234.56`, `$25.99` → `25.99`.
 *
 * İkisi birden varsa SONDAKİ ondalık ayırıcı; tek tür birden çok kez geçiyorsa
 * binlik ayırıcı. Tek virgül ondalık sayılıyor: virgülün sütun ayırdığı satırlar
 * buraya virgülsüz parçalar getiriyor.
 */
function sayiMetni(raw: string): string {
  const s = raw.replace(/[\s$]/g, '');
  const virgul = s.lastIndexOf(',');
  const nokta = s.lastIndexOf('.');
  if (virgul >= 0 && nokta >= 0) {
    return virgul > nokta
      ? s.replace(/\./g, '').replace(',', '.')
      : s.replace(/,/g, '');
  }
  if (virgul >= 0) return s.split(',').length > 2 ? s.replace(/,/g, '') : s.replace(',', '.');
  if (nokta >= 0 && s.split('.').length > 2) return s.replace(/\./g, '');
  return s;
}

/**
 * Serbest metni satırlara çevirir.
 *
 * Kabul edilen biçimler:
 *   · `B08N5WRWNW`
 *   · `B08N5WRWNW,25.99`         → alış fiyatı
 *   · `B08N5WRWNW,25.99,3`       → alış fiyatı + adet
 *   · `https://www.amazon.com/dp/B08N5WRWNW`
 *
 * Fiyat isteğe bağlı: kullanıcı yalnız ASIN verirse ürün yine ekleniyor,
 * fiyatı kargo maliyeti gelince hesaplanıyor. Zorunlu tutsaydık, elinde
 * yalnızca ASIN listesi olan kullanıcı bu yolu hiç kullanamazdı.
 */
export function parseDirectAddInput(
  input: string,
  limit: number = DIRECT_ADD_SATIR_SINIRI,
): ParsedDirectAdd {
  const lines: DirectAddLine[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  let invalidCount = 0;
  let totalLines = 0;
  let duplicates = 0;
  let droppedOverLimit = 0;

  for (const ham of input.split(/\r?\n/)) {
    const satir = ham.trim();
    if (satir === '') continue;
    totalLines++;

    if (lines.length >= limit) {
      droppedOverLimit++;
      continue;
    }

    const parcalar = satiriBol(satir).map((p) => p.trim());
    const asin = extractAsin(parcalar[0] ?? '');

    if (asin === null) {
      invalidCount++;
      if (invalid.length < 50) invalid.push(satir.slice(0, 80));
      continue;
    }

    if (seen.has(asin)) {
      duplicates++;
      continue;
    }
    seen.add(asin);

    lines.push({
      asin,
      sourcePriceUsd: pozitifSayi(parcalar[1]),
      qty: tamSayi(parcalar[2]),
      raw: satir.slice(0, 120),
    });
  }

  return { lines, invalid, invalidCount, totalLines, duplicates, droppedOverLimit };
}

/**
 * Sayı okuma.
 *
 * Sekmeli/noktalı virgüllü satırda hem `25.99` hem `25,99` doğru okunuyor.
 * Yalnız virgülle ayrılmış satırda `B08N5WRWNW,25,99` hâlâ belirsiz (25 dolar
 * 99 adet mi, 25,99 dolar mı?) ve ilki seçiliyor; Excel'den yapıştırma sekmeli
 * geldiği için asıl yol bu değil. Değer **pozitif** olmalı ve makul aralıkta
 * kalmalı; aksi hâlde yok sayılıyor.
 */
function pozitifSayi(raw: string | undefined): number | null {
  if (raw === undefined || raw === '') return null;
  const n = Number(sayiMetni(raw));
  if (!Number.isFinite(n) || n <= 0 || n > 1_000_000) return null;
  return n;
}

function tamSayi(raw: string | undefined): number | null {
  if (raw === undefined || raw === '') return null;
  const n = Number(sayiMetni(raw));
  if (!Number.isInteger(n) || n < 0 || n > 100_000) return null;
  return n;
}

/**
 * Kâr bantlarından mağazanın tek bir varsayılan stok adedi çıkıyor mu?
 *
 * ⚠️ Neden gerekli. Bant adedi ürün başına MALİYETE göre seçiliyor
 * (`costFrom <= maliyet <= costTo`). Doğrudan eklenen ürünün maliyeti ise
 * eklendiği anda bilinmiyor: kargo fiyatı yok, çoğunda alış fiyatı da yok.
 * Yani "bu ürüne hangi bandın adedi düşer" sorusu o anda cevapsız.
 *
 * Cevap yalnızca bütün bantlar AYNI adeti taşıyorsa net: üretimde tek profil,
 * beş bant, hepsi 20. Bantlar farklıysa tahmin yürütülmüyor, kullanıcı tek bir
 * adet giriyor.
 *
 * 0 da bir değer olarak döner; "0 varsayılan sayılır mı" kararı çağıranın.
 * API de ekran da 0'ı sessiz varsayılan olarak KULLANMIYOR (bkz.
 * `directAddRunSchema`).
 */
export type MagazaVarsayilanAdedi =
  { tur: 'TEK'; adet: number } | { tur: 'FARKLI'; adetler: number[] } | { tur: 'YOK' };

export function magazaVarsayilanAdedi(
  tiers: ReadonlyArray<{ defaultQty: number }>,
): MagazaVarsayilanAdedi {
  if (tiers.length === 0) return { tur: 'YOK' };
  const adetler = [...new Set(tiers.map((t) => t.defaultQty))].sort((a, b) => a - b);
  return adetler.length === 1 ? { tur: 'TEK', adet: adetler[0]! } : { tur: 'FARKLI', adetler };
}

/** Varsayılan stok adedinin üst sınırı — ekran ve API aynı sınırı kullanıyor. */
export const DIRECT_ADD_VARSAYILAN_ADET_SINIRI = 10_000;

/*
 * ⚠️ Mesajlar Türkçe ve ne yapılacağını söylüyor. zod'un varsayılanı
 * ("Too big: expected string to have <=4000000 characters") ekranda ham alan
 * adıyla "input: Too big…" diye görünüyordu. Fastify gövde sınırı 60 MB,
 * yani ~30 bin bağlantılık bir yapıştırma bu kontrole gerçekten ulaşıyor.
 * Önizleme ile ekleme aynı sınırı kullanıyor.
 */
export const directAddListeSemasi = z
  .string({ message: 'ASIN listesi metin olmalı.' })
  .min(1, 'ASIN listesi boş.')
  .max(4_000_000, 'Liste çok uzun (en fazla 4 MB). Listeyi bölüp parça parça ekleyin.');

/**
 * `POST /discovery/direct-add` gövdesi.
 *
 * ⚠️ `defaultQty` eskiden `.default(0)` idi ve ekrandaki alan '0' ile
 * başlıyordu. 31 Ağustos 2026 üretim: 7.991 satırlık listede hiçbir satırda
 * adet yoktu, alan '0'da kaldı ve **1.592 ürün stok 0 ile** eklendi. Bunların
 * 172'si Amazon'da stok 0 ile açıldı (örneklenen 20 SKU'nun 20'si Amazon'da 0);
 * hiçbir yol bu adedi sonradan düzeltmiyordu.
 *
 * Artık:
 *   · Adet verilmezse `undefined` kalıyor; API kâr bandından çözüyor, çözemezse
 *     reddediyor. Sessiz 0 yok.
 *   · 0 bilerek verilebilir, ama `sifirAdetOnay: true` olmadan geçmiyor.
 */
export const directAddRunSchema = z
  .object({
    input: directAddListeSemasi,
    /*
     * Varsayılan AÇIK. Kısıtlı bir ürünü listelemek Amazon'un doğrudan
     * yaptırım uyguladığı durumlardan biri; kapatmak bilinçli bir karar
     * olmalı, varsayılan değil.
     */
    checkRestrictions: z.boolean().default(true),
    defaultQty: z
      .number({ message: 'Varsayılan stok adedi sayı olmalı.' })
      .int('Varsayılan stok adedi tam sayı olmalı.')
      .min(0, 'Varsayılan stok adedi negatif olamaz.')
      .max(DIRECT_ADD_VARSAYILAN_ADET_SINIRI, 'Varsayılan stok adedi en fazla 10.000 olabilir.')
      .optional(),
    sifirAdetOnay: z.boolean().default(false),
  })
  .refine((d) => d.defaultQty !== 0 || d.sifirAdetOnay, {
    path: ['defaultQty'],
    message:
      "Varsayılan stok adedi 0: ürünler Amazon'da listelenmez. Bilerek istiyorsanız onay kutusunu işaretleyin.",
  });

export type DirectAddRunInput = z.infer<typeof directAddRunSchema>;

/** Ürünün neden eklenmediği. */
export type DirectAddSkipReason =
  | 'INVALID_ASIN'
  | 'DUPLICATE'
  | 'ALREADY_IN_STORE'
  | 'DELETED_IN_STORE'
  | 'BLACKLIST_ASIN'
  | 'COMMON_POOL'
  | 'BLACKLIST_BRAND'
  | 'BLACKLIST_KEYWORD'
  | 'BLACKLIST_CATEGORY'
  | 'RESTRICTED'
  | 'ERROR';

export const DIRECT_ADD_SKIP_LABELS: Record<DirectAddSkipReason, string> = {
  INVALID_ASIN: 'ASIN biçimi geçersiz',
  DUPLICATE: 'Listede birden fazla kez var',
  ALREADY_IN_STORE: 'Mağazanızda zaten var',
  DELETED_IN_STORE: 'Mağazanızda silinmiş kaydı var',
  BLACKLIST_ASIN: 'Yasaklı ürün listenizde',
  COMMON_POOL: 'Ortak havuzda işaretli',
  BLACKLIST_BRAND: 'Yasaklı marka',
  BLACKLIST_KEYWORD: 'Başlıkta yasaklı kelime',
  BLACKLIST_CATEGORY: 'Yasaklı kategori',
  RESTRICTED: 'Amazon bu ürünü listelemenize izin vermiyor',
  ERROR: 'Eklenirken hata oluştu',
};
